import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { CiSearch } from "react-icons/ci";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "@mui/material";
import api from "../../../Services/api";
import CustomSnackbar from "../../../Component/CustomSnackBar";
import Button from "../../../Component/Button";
import UploadActsRules from "./UploadActsRules";
import {
  MdOutlineKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from "react-icons/md";
import ReactPaginate from "react-paginate";
import DeleteModal from "./DeleteModal";

const ActsRulesPage = () => {
  const [allActs, setAllActs] = useState([]);
  const [allRules, setAllRules] = useState([]);
  const [showupload, setShowUpload] = useState(false);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [isEdit, setIsEdit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const isFiltering = searchTerm.trim() !== "";

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [activeState, setActiveState] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const isActPage = location.pathname === "/acts";
  const items = isActPage ? allActs : allRules;

  useEffect(() => {
    getAllStates();
    if (selectedState) {
      getActsRulesByState(selectedState);
    } else {
      getAllActsAndRules();
    }
  }, [selectedState, isActPage]);

  // suggestions logic
  useEffect(() => {
    const filteredItems = selectedState
      ? items.filter((item) => item.State === selectedState)
      : items;

    if (!searchTerm.trim()) {
      // show all titles when focused
      setSuggestions(filteredItems.map((i) => i.Title));
    } else {
      const matched = filteredItems
        .filter((i) => i.Title.toLowerCase().includes(searchTerm.toLowerCase()))
        .map((i) => i.Title);
      setSuggestions(matched.length ? matched : ["No suggestions found"]);
    }
  }, [searchTerm, selectedState, items]);

  // highlight function
  const highlightMatch = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} className="bg-yellow-200 font-medium">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // filtered data for display with recent first
  const filteredItems = items
    .filter((item) => {
      const matchesState = selectedState ? item.State === selectedState : true;
      const matchesSearch = searchTerm
        ? item.Title.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      return matchesState && matchesSearch;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // recent first

  const getAllStates = async () => {
    try {
      const res = await api.common.getAllStatesApi();
      setStates(res.data.states || []);
    } catch (err) {
      console.log("Error fetching states:", err);
    }
  };

  const getAllActsAndRules = async (page = 1) => {
    try {
      setLoading(true);
      if (isActPage) {
        const res = await api.ActsAndRules.getAllActs(page, pageSize);
        setAllActs(
          res.data.data.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
        );
        setPageCount(res.data.pagination.totalPages);
        setCurrentPage(res.data.pagination.currentPage);
      } else {
        const res = await api.ActsAndRules.getAllRules(page, pageSize);
        setAllRules(
          res.data.data.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
        );
        setPageCount(res.data.pagination.totalPages);
        setCurrentPage(res.data.pagination.currentPage);
      }
      setTimeout(() => setLoading(false), 1000);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const getActsRulesByState = async (state, page = 1) => {
    try {
      setLoading(true);
      setActiveState(state);

      if (isActPage) {
        const res = await api.ActsAndRules.getActsByState(
          state,
          page,
          pageSize
        );
        setAllActs(res.data.data || []);
        setPageCount(res.data.pagination.totalPages);
        setCurrentPage(res.data.pagination.currentPage);
      } else {
        const res = await api.ActsAndRules.getRulesByState(
          state,
          page,
          pageSize
        );
        setAllRules(res.data.data || []);
        setPageCount(res.data.pagination.totalPages);
        setCurrentPage(res.data.pagination.currentPage);
      }

      setTimeout(() => setLoading(false), 500);
    } catch (err) {
      console.log("Error fetching state data:", err);
    }
  };

  const handleChange = (event) => setSelectedState(event.target.value);

  const handleDeleteConfirm = async () => {
    try {
      const res = await api.ActsAndRules.deleteActAndRules(deleteId);

      if (res.status === 200 || res.status === 204) {
        if (isActPage) {
          setAllActs((prev) => prev.filter((item) => item._id !== deleteId));
        } else {
          setAllRules((prev) => prev.filter((item) => item._id !== deleteId));
        }

        triggerSnackbar("success", "Deleted successfully");
      }
    } catch (err) {
      console.log("Delete error:", err);
    }

    setShowDelete(false);
  };

  useEffect(() => {
    if (!loading) return;

    if (filteredItems.length === 0) {
      setLoading(false);
    }
  }, [filteredItems, loading]);

  const handleSelectSuggestion = (value) => {
    if (value !== "No suggestions found") setSearchTerm(value);
    setShowDropdown(false);
  };

  const handleClose = () => setSnackbar({ ...snackbar, open: false });
  const triggerSnackbar = (type, message) => {
    setSnackbar({ open: true, message, severity: type });
  };

  const handlePagination = (page) => {
    if (activeState) {
      getActsRulesByState(activeState, page);
    } else {
      getAllActsAndRules(page);
    }
  };

  return (
    <section className="lg:px-12 lg:py-12 bg-[#F5F5F5]   h-full">
      <CustomSnackbar
        open={snackbar.open}
        handleClose={handleClose}
        message={snackbar.message}
        severity={snackbar.severity}
      />
      <div className="flex flex-col gap-8 overflow-auto">
        <div className="flex flex-col gap-8">
          <div className="flex justify-between items-center lg:h-[44px]">
            {/* Toggle Buttons */}
            <div className="flex  justify-center items-center  bg-white rounded-2xl">
              <button
                onClick={() => navigate("/acts")}
                className={`px-4 py-2 ${
                  isActPage
                    ? "bg-[#F5E0DF] rounded-2xl text-[#CD2224]"
                    : "text-gray-400"
                }`}
              >
                Acts
              </button>
              <button
                onClick={() => navigate("/rules")}
                className={`px-4 py-2 ${
                  !isActPage
                    ? "bg-[#F5E0DF] rounded-2xl text-[#CD2224]"
                    : "text-gray-400"
                }`}
              >
                Rules
              </button>
            </div>

            {/* Filters */}
            <div className="flex gap-6 h-[44px] ">
              <Box
                sx={{
                  minWidth: 158,
                  height: "44px",
                  backgroundColor: "white",
                  borderRadius: "16px",
                }}
              >
                <FormControl fullWidth>
                  <Select
                    value={selectedState}
                    onChange={handleChange}
                    displayEmpty
                    disableUnderline
                    sx={{
                      height: "44px",
                      width: "158px",
                      bgcolor: "white",
                      borderRadius: "16px",
                      fontSize: "14px",
                    }}
                  >
                    <MenuItem value="" disabled className="text-[13px]">
                      Central/State
                    </MenuItem>
                    {states.map((s) => (
                      <MenuItem key={s.id} value={s.State}>
                        {s.State}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Search Box */}
              <div className="flex flex-col w-[250px] relative">
                <div className="flex items-center gap-2 lg:px-5 lg:py-3 rounded-2xl border border-black bg-white relative">
                  <CiSearch className="text-gray-500 text-xl" />
                  <input
                    type="text"
                    placeholder="Search Acts/Rules"
                    className="bg-transparent outline-none flex-1 text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                  />
                </div>

                {/* Dropdown */}
                {showDropdown && suggestions.length > 0 && (
                  <div className="absolute top-[50px] left-0 w-full bg-white border border-gray-300 rounded-2xl max-h-48 overflow-auto shadow-md z-10">
                    {suggestions.map((s, idx) => (
                      <div
                        key={idx}
                        className={`px-4 py-2 text-sm cursor-pointer ${
                          s === "No suggestions found"
                            ? "text-gray-400 cursor-default"
                            : "hover:bg-gray-100"
                        }`}
                        onClick={() => handleSelectSuggestion(s)}
                      >
                        {s === "No suggestions found"
                          ? s
                          : highlightMatch(s, searchTerm)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Page Title */}
          <div className="flex justify-between items-start ">
            <div className="w-lg:w-[737px]">
              {isActPage ? (
                <div className="flex flex-col gap-4">
                  <h1 className="font-semibold xl:text-[39px] leading-[120%]">
                    Acts
                  </h1>
                  <p className="font-normal text-base leading-6 text-start lg:w-[640px]">
                    “Acts” is a primary piece of legislation passed by
                    legislature. It is a comprehensive and broad legal framework
                    on specific subject matter covering various topics, which in
                    return also provide basis for compliance, enforcement, and
                    dispute resolution concerning the subject matter they
                    address.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <h1 className="font-semibold xl:text-[39px] leading-[120%] text-start">
                    Rules
                  </h1>
                  <p className="font-normal text-base leading-6 lg:w-[640px]">
                    “Rules” are legislative piece providing guidelines which in
                    turn defines the procedures for performing and implementing
                    the concerned Act. Rules are inherently secondary and
                    complimentary to the concerned Acts.
                  </p>
                </div>
              )}
            </div>
            <Button
              label="Upload"
              onClick={() => {
                setShowUpload(true);
                setIsEdit(null);
              }}
            />
          </div>
        </div>

        {/* List */}
        <div className="flex flex-col gap-8">
          {loading ? (
            <div
              className="d-flex flex-row align-items-center justify-content-center"
              style={{ marginTop: "80px" }}
            >
              <h3>Loading</h3>
              <div
                className="spinner-grow text-danger"
                style={{ width: "1rem", height: "1rem", marginLeft: "10px" }}
                role="status"
              ></div>
              <div
                className="spinner-grow text-danger"
                style={{ width: "1rem", height: "1rem", marginLeft: "10px" }}
                role="status"
              ></div>
              <div
                className="spinner-grow text-danger"
                style={{ width: "1rem", height: "1rem", marginLeft: "10px" }}
                role="status"
              ></div>
            </div>
          ) : filteredItems.length === 0 ? (
            <p>No {isActPage ? "Acts" : "Rules"} Found</p>
          ) : (
            filteredItems.map((item) => (
              <div
                key={item._id}
                className="flex flex-col bg-white rounded-2xl lg:p-6 lg:gap-3"
              >
                <h1 className="font-semibold text-sm">{item.State}</h1>
                <div className="flex justify-between">
                  <p className="text-base">
                    {highlightMatch(item.Title, searchTerm)}
                  </p>

                  {/* actions */}
                  <div className="flex gap-4">
                    {/* View (show NA if no file uploaded) */}
                    {item.FileUrl ? (
                      <div
                        className="group hover:border-none hover:bg-black flex justify-between items-center w-[48px] h-[44px] rounded-2xl bg-[#F5E0DF] lg:py-2 lg:px-4 cursor-pointer"
                        title="View document"
                        onClick={() => window.open(item.FileUrl, "_blank")}
                      >
                        <button className="text-[#CD2224] group-hover:text-white">
                          <svg
                            width="18"
                            height="20"
                            viewBox="0 0 18 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14.9755 19V13M17 15L15.0038 13L13.0076 15M8.98488 1H4.19395C3.07597 1 2.51697 1 2.08996 1.21799C1.71434 1.40973 1.40896 1.71569 1.21758 2.09202C1 2.51984 1 3.0799 1 4.2V15.8C1 16.9201 1 17.4802 1.21758 17.908C1.40896 18.2843 1.71434 18.5903 2.08996 18.782C2.51697 19 3.07597 19 4.19395 19H10.9811M8.98488 1L14.9735 7M8.98488 1V5.4C8.98488 5.96005 8.98488 6.24008 9.09367 6.45399C9.18939 6.64215 9.342 6.79513 9.52985 6.89101C9.74334 7 10.0228 7 10.5819 7H14.9735M14.9735 7V9M4.99244 15H8.98488M4.99244 11H10.9811M4.99244 7H5.99055"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-center items-center text-gray-500">
                        N/A
                      </div>
                    )}

                    {/* Edit */}
                    <div
                      className="group hover:bg-black hover:border-none flex w-[48px] h-[44px] justify-between items-center rounded-2xl bg-[#F5E0DF] lg:py-2 lg:px-4 cursor-pointer"
                      title="Edit"
                      onClick={() => {
                        setIsEdit(item);
                        setShowUpload(true);
                      }}
                    >
                      <button className="text-[#CD2224] group-hover:text-white">
                        <svg
                          width="18"
                          height="20"
                          viewBox="0 0 18 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.79811 1H4.11925C3.02741 1 2.48149 1 2.06446 1.21799C1.69763 1.40973 1.39939 1.71569 1.21249 2.09202C1 2.51984 1 3.0799 1 4.2V15.8C1 16.9201 1 17.4802 1.21249 17.908C1.39939 18.2843 1.69763 18.5903 2.06446 18.782C2.48149 19 3.02741 19 4.11925 19H5.87382M8.79811 1L14.6467 7M8.79811 1V5.4C8.79811 5.96005 8.79811 6.24008 8.90436 6.45399C8.99784 6.64215 9.14688 6.79513 9.33034 6.89101C9.53884 7 9.81177 7 10.3577 7H14.6467M14.6467 7V8M4.89906 15H7.33597M4.89906 11H9.77288M4.89906 7H5.87382M9.77288 19L11.7468 18.595C11.9188 18.5597 12.0049 18.542 12.0851 18.5097C12.1564 18.4811 12.2241 18.4439 12.2868 18.399C12.3576 18.3484 12.4196 18.2848 12.5437 18.1574L16.5962 14C17.1346 13.4477 17.1346 12.5523 16.5962 12C16.0579 11.4477 15.1851 11.4477 14.6467 12L10.5942 16.1574C10.47 16.2848 10.408 16.3484 10.3587 16.421C10.3149 16.4853 10.2787 16.5548 10.2508 16.6279C10.2193 16.7102 10.2021 16.7985 10.1677 16.975L9.77288 19Z"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Delete */}
                    <div
                      className="group hover:bg-black hover:text-white hover:border-none w-[48px] h-[44px] flex justify-between items-center rounded-2xl bg-[#F5E0DF] lg:py-2 lg:px-4 cursor-pointer"
                      title="Delete"
                      onClick={() => {
                        setDeleteId(item._id);
                        setShowDelete(true);
                      }}
                    >
                      <button className="text-[#CD2224] group-hover:text-white">
                        <svg
                          width="18"
                          height="20"
                          viewBox="0 0 18 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M15 4L14.1991 16.0129C14.129 17.065 14.0939 17.5911 13.8667 17.99C13.6666 18.3412 13.3648 18.6235 13.0011 18.7998C12.588 19 12.0607 19 11.0062 19H6.99377C5.93927 19 5.41202 19 4.99889 18.7998C4.63517 18.6235 4.33339 18.3412 4.13332 17.99C3.90607 17.5911 3.871 17.065 3.80086 16.0129L3 4M1 4H17M13 4L12.7294 3.18807C12.4671 2.40125 12.3359 2.00784 12.0927 1.71698C11.8779 1.46013 11.6021 1.26132 11.2905 1.13878C10.9376 1 10.523 1 9.6936 1H8.3064C7.477 1 7.0624 1 6.70951 1.13878C6.39792 1.26132 6.12208 1.46013 5.90729 1.71698C5.66405 2.00784 5.53292 2.40125 5.27064 3.18807L5 4M11 8V15M7 8V15"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {!isFiltering && pageCount > 1 && (
        <div className="flex justify-center items-center gap-3 mt-6">
          <ReactPaginate
            breakLabel="..."
            nextLabel={<MdKeyboardArrowRight className="text-3xl font-bold" />}
            previousLabel={
              <MdOutlineKeyboardArrowLeft className="text-3xl font-bold" />
            }
            onPageChange={(e) => handlePagination(e.selected + 1)}
            pageRangeDisplayed={2}
            marginPagesDisplayed={1}
            pageCount={pageCount}
            forcePage={currentPage - 1}
            containerClassName="flex items-center gap-3 text-sm font-semibold"
            pageClassName="px-3 py-1 rounded-lg border border-black hover:bg-black hover:text-white cursor-pointer"
            activeClassName="bg-black text-white"
            disabledClassName="opacity-50 cursor-not-allowed"
          />
        </div>
      )}

      {/* Upload Modal */}
      <Modal
        open={showupload}
        onClose={() => setShowUpload(false)}
        aria-labelledby="upload-modal-title"
        aria-describedby="upload-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxHeight: "90vh",
            height: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            overflowY: "auto",
          }}
        >
          <UploadActsRules
            onClose={() => {
              setShowUpload(false);
              setIsEdit(null);
            }}
            isEdit={isEdit}
            triggerSnackbar={triggerSnackbar}
          />
        </Box>
      </Modal>

      <Modal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "608px",
            bgcolor: "white",
            borderRadius: "16px",
            boxShadow: 24,
            p: 0,
          }}
        >
          <DeleteModal
            onCancel={() => setShowDelete(false)}
            onDelete={handleDeleteConfirm}
          />
        </Box>
      </Modal>
    </section>
  );
};

export default ActsRulesPage;
